import React, { useState, useCallback, useRef, useEffect, useMemo, useId } from 'react';
import { ChevronDown, ChevronRight, Copy, Check } from 'lucide-react';
import { EmailTemplateDetail } from '@app-types';
import { extractDataPlaceholders, describePlaceholder } from '@utils/subjectPlaceholders';
import styles from './PlaceholderReference.module.css';

export interface PlaceholderReferenceProps {
  /** The currently selected template detail, or undefined when none is selected. */
  template: EmailTemplateDetail | undefined;
}

/** Render a placeholder key in its `{{key}}` token form. */
const toToken = (key: string): string => `{{${key}}}`;

/**
 * Collapsible reference panel listing the DATA placeholders used by the
 * currently selected template, each with a description and a Copy button.
 *
 * Placeholders are derived from the template's own content — default subject,
 * text-block defaults, and the raw HTML — so the list is exactly what that
 * template uses (works for notification and legacy Meetings templates alike).
 * Structural `TEXT_*` tokens are excluded. Renders nothing when no template is
 * selected. Collapsed by default.
 */
const PlaceholderReference: React.FC<PlaceholderReferenceProps> = ({ template }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const copyTimerRef = useRef<number | null>(null);
  // Stable, instance-unique id linking the toggle (aria-controls) to the
  // expandable region — safe even if two panels mount open at once.
  const regionId = useId();

  // Derive the data placeholders from the template's content, in first-seen
  // order across subject -> text-block defaults -> html.
  const placeholders = useMemo(() => {
    if (!template) return [];
    const blockDefaults = template.text_blocks_schema.map((field) => field.default);
    return extractDataPlaceholders(template.default_subject, ...blockDefaults, template.html_template);
  }, [template]);

  // Clear any pending "Copied" reset timer on unmount so it never fires against
  // an unmounted component.
  useEffect(
    () => () => {
      if (copyTimerRef.current !== null) {
        window.clearTimeout(copyTimerRef.current);
      }
    },
    []
  );

  const handleCopy = useCallback(async (key: string) => {
    try {
      await navigator.clipboard.writeText(toToken(key));
      setCopiedKey(key);
      // Clear a prior pending timer before starting a new one so rapid copies
      // don't leave orphaned timers.
      if (copyTimerRef.current !== null) {
        window.clearTimeout(copyTimerRef.current);
      }
      copyTimerRef.current = window.setTimeout(() => {
        setCopiedKey((current) => (current === key ? null : current));
        copyTimerRef.current = null;
      }, 1500);
    } catch {
      // Clipboard access can be denied (e.g. insecure context); fail silently.
    }
  }, []);

  // Nothing to show until a template is selected.
  if (!template) return null;

  return (
    <section className={styles.reference} data-test-id="placeholder-reference">
      <button
        type="button"
        className={styles.toggle}
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        aria-controls={regionId}
        data-test-id="placeholder-reference-toggle"
      >
        {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <span className={styles.toggleLabel}>Available placeholders</span>
        <span className={styles.toggleHint}>
          {placeholders.length === 0
            ? 'This template uses no dynamic placeholders'
            : `${placeholders.length} available in this template — click to copy`}
        </span>
      </button>

      {isOpen &&
        (placeholders.length === 0 ? (
          <p id={regionId} className={styles.emptyState} data-test-id="placeholder-empty">
            This template has no dynamic placeholders. Its content is fully static text.
          </p>
        ) : (
          <ul id={regionId} className={styles.placeholderList} data-test-id="placeholder-list">
            {placeholders.map((key) => {
              const token = toToken(key);
              const copied = copiedKey === key;
              return (
                <li key={key} className={styles.placeholderRow} data-test-id={`placeholder-row-${key}`}>
                  <code className={styles.token}>{token}</code>
                  <span className={styles.description}>{describePlaceholder(key)}</span>
                  <button
                    type="button"
                    className={styles.copyButton}
                    onClick={() => handleCopy(key)}
                    aria-label={`Copy ${token}`}
                    data-test-id={`placeholder-copy-${key}`}
                  >
                    {copied ? <Check size={14} /> : <Copy size={14} />}
                    <span>{copied ? 'Copied' : 'Copy'}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        ))}
    </section>
  );
};

export default PlaceholderReference;
